import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DECISIONS = [
  { v: "CHANGE_MANUAL", label: "Change the Manual" },
  { v: "CHANGE_PRACTICE", label: "Change the Practice" },
];
const dlabel = (v) => (DECISIONS.find((d) => d.v === v) || {}).label || v;
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" }) : "—");

function GapClose({ tt, gap, onSaved }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const save = async () => {
    try {
      const res = await axios.put(`${API}/tabletops/${tt.id}/gaps/${gap.gap_id}/close`, { closure_note: note });
      onSaved(res.data);
      toast.success(`${tt.tt_number} ${gap.gap_number} closed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Close failed");
    }
  };
  if (!open) return <button className="vr-link" onClick={() => setOpen(true)} data-testid={`ttx-gap-close-${tt.tt_number}-${gap.gap_number}`}>Close…</button>;
  return (
    <div className="vr-form">
      <input className="inp" placeholder="What actually changed (manual edit made / practice fixed)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`ttx-gap-note-${tt.tt_number}-${gap.gap_number}`} />
      <div className="vr-btnrow">
        <button className="btn" onClick={save} disabled={!note.trim()} data-testid={`ttx-gap-save-${tt.tt_number}-${gap.gap_number}`}>Save</button>
        <button className="btn ghost" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

function AddGap({ tt, onSaved }) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [decision, setDecision] = useState("");
  const [owner, setOwner] = useState("");
  const [due, setDue] = useState("");
  const save = async () => {
    try {
      const res = await axios.post(`${API}/tabletops/${tt.id}/gaps`, { description, decision, owner, due_date: due });
      onSaved(res.data);
      toast.success(`Gap logged on ${tt.tt_number} — decision: ${dlabel(decision)}`);
      setDescription(""); setDecision(""); setOwner(""); setDue(""); setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not log the gap");
    }
  };
  if (!open) return <button className="vr-link" onClick={() => setOpen(true)} data-testid={`ttx-add-gap-${tt.tt_number}`}>+ Log a gap found</button>;
  return (
    <div className="vr-form" data-testid={`ttx-gap-form-${tt.tt_number}`}>
      <input className="inp" placeholder="The gap — where the manual and reality diverged" value={description} onChange={(e) => setDescription(e.target.value)} data-testid={`ttx-gap-desc-${tt.tt_number}`} />
      <Select value={decision} onValueChange={setDecision}>
        <SelectTrigger className="inp sel" data-testid={`ttx-gap-decision-${tt.tt_number}`}><SelectValue placeholder="The call — no third option…" /></SelectTrigger>
        <SelectContent>{DECISIONS.map((d) => (<SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>))}</SelectContent>
      </Select>
      <input className="inp" placeholder="Owner (one name)" value={owner} onChange={(e) => setOwner(e.target.value)} data-testid={`ttx-gap-owner-${tt.tt_number}`} />
      <input className="inp" type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid={`ttx-gap-due-${tt.tt_number}`} />
      <div className="vr-btnrow">
        <button className="btn" onClick={save} disabled={!description.trim() || !decision || !owner.trim()} data-testid={`ttx-gap-submit-${tt.tt_number}`}>Log gap</button>
        <button className="btn ghost" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

function ExerciseCard({ tt, onSaved }) {
  const gaps = tt.gaps || [];
  const openGaps = gaps.filter((g) => g.status === "OPEN").length;
  const closeExercise = async () => {
    try {
      const res = await axios.put(`${API}/tabletops/${tt.id}/close`, { closing_note: "" });
      onSaved(res.data);
      toast.success(`${tt.tt_number} closed — all gaps resolved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Close failed");
    }
  };
  return (
    <div className="formcard" data-testid={`ttx-card-${tt.tt_number}`} style={{ marginBottom: 18 }}>
      <div className="formcard-title">
        {tt.tt_number} · {tt.scenario_title}{" "}
        <span className={`vr-pill ${tt.status === "CLOSED" ? "cleared" : "open"}`} data-testid={`ttx-status-${tt.tt_number}`}>
          {tt.status}{tt.status === "CLOSED" ? ` ${fmt(tt.closed_at)}` : ""}
        </span>
      </div>
      <p style={{ margin: "4px 0 10px", fontSize: 14 }}>
        Run {tt.date_run} · Facilitator: {tt.facilitator}{tt.participants ? ` · Participants: ${tt.participants}` : ""}
        {tt.recorded_by ? <span className="cap-sub" style={{ display: "block" }}>Logged by {tt.recorded_by.name} on {fmt(tt.created_at)}</span> : null}
      </p>
      <p style={{ margin: "0 0 6px", fontSize: 14 }}><strong>Scenario:</strong> {tt.scenario}</p>
      {tt.manual_says ? <p style={{ margin: "0 0 6px", fontSize: 14 }}><strong>What the manual says:</strong> {tt.manual_says}</p> : null}
      {tt.actually_happened ? <p style={{ margin: "0 0 10px", fontSize: 14 }}><strong>What we actually did / could do:</strong> {tt.actually_happened}</p> : null}
      <table data-testid={`ttx-gaps-${tt.tt_number}`}>
        <tbody>
          <tr><th>Gap</th><th>What diverged</th><th>The call</th><th>Owner / due</th><th style={{ width: 230 }}>Status</th></tr>
          {gaps.length === 0 ? (
            <tr><td colSpan={5}>No gaps logged {tt.status === "CLOSED" ? "— clean run." : "yet."}</td></tr>
          ) : (
            gaps.map((g) => (
              <tr key={g.gap_id}>
                <td className="q">{g.gap_number}</td>
                <td>{g.description}</td>
                <td className="q">{dlabel(g.decision)}</td>
                <td>{g.owner}{g.due_date ? <div className="cap-sub">due {g.due_date}</div> : null}</td>
                <td>
                  {g.status === "CLOSED" ? (
                    <>
                      <span className="vr-pill cleared" data-testid={`ttx-gap-status-${tt.tt_number}-${g.gap_number}`}>CLOSED {fmt(g.closed_at)}</span>
                      <div className="cap-sub">{g.closure_note}{g.closed_by ? ` — ${g.closed_by.name}` : ""}</div>
                    </>
                  ) : (
                    <>
                      <span className="vr-pill open" data-testid={`ttx-gap-status-${tt.tt_number}-${g.gap_number}`}>OPEN</span>
                      {tt.status === "OPEN" && <GapClose tt={tt} gap={g} onSaved={onSaved} />}
                    </>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {tt.status === "OPEN" && (
        <div style={{ marginTop: 10, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          <AddGap tt={tt} onSaved={onSaved} />
          <button className="btn ghost" onClick={closeExercise} disabled={openGaps > 0} title={openGaps > 0 ? `${openGaps} gap(s) still open` : "All gaps closed — close the exercise"} data-testid={`ttx-close-${tt.tt_number}`}>
            Close exercise{openGaps > 0 ? ` (${openGaps} gap${openGaps > 1 ? "s" : ""} open)` : ""}
          </button>
        </div>
      )}
      {tt.status === "CLOSED" && tt.closed_by ? <p className="cap-sub" style={{ marginTop: 8 }}>Closed by {tt.closed_by.name}</p> : null}
    </div>
  );
}

export default function TabletopLog({ active }) {
  const [rows, setRows] = useState(null);
  const [title, setTitle] = useState("");
  const [dateRun, setDateRun] = useState("");
  const [facilitator, setFacilitator] = useState("");
  const [participants, setParticipants] = useState("");
  const [scenario, setScenario] = useState("");
  const [manualSays, setManualSays] = useState("");
  const [actual, setActual] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchRows = useCallback(() => {
    axios.get(`${API}/tabletops`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { if (active === "ttx") fetchRows(); }, [active, fetchRows]);

  const canSubmit = title.trim() && dateRun && facilitator.trim() && scenario.trim() && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/tabletops`, {
        scenario_title: title, date_run: dateRun, facilitator, participants,
        scenario, manual_says: manualSays, actually_happened: actual,
      });
      toast.success(`${res.data.tt_number} logged — now record every gap the drill exposed`);
      setTitle(""); setDateRun(""); setFacilitator(""); setParticipants(""); setScenario(""); setManualSays(""); setActual("");
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not log the exercise");
    }
    setSubmitting(false);
  };

  const onSaved = (d) => setRows((p) => p.map((x) => (x.id === d.id ? d : x)));
  const openCount = (rows || []).filter((r) => r.status === "OPEN").length;

  return (
    <section className={`chapter utility${active === "ttx" ? " active" : ""}`} id="ttx">
      <div className="eyebrow">Working record · drills, not paperwork — run the scenario, log where the manual and reality diverge, force the fix</div>
      <h1>Tabletop Exercise Log</h1>
      <p className="lede">Twenty minutes of "the license expired Tuesday and she worked two cases Thursday — what does the manual say happens now?" exposes more than a month of chapter-reading. Log each drill here. Every gap found gets the mandatory call — <strong>change the manual, or change the practice</strong> — with one owner and a due date. No third option of noting it and moving on. Exercises stay OPEN until every gap is closed with evidence of what changed.</p>

      <div className="formcard" data-testid="ttx-form">
        <div className="formcard-title">Log a tabletop exercise</div>
        <div className="fld-grid">
          <div className="fld"><label>Scenario title</label><input className="inp" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. License lapse mid-week" data-testid="ttx-title" /></div>
          <div className="fld"><label>Date run</label><input className="inp" type="date" value={dateRun} onChange={(e) => setDateRun(e.target.value)} data-testid="ttx-date" /></div>
        </div>
        <div className="fld-grid">
          <div className="fld"><label>Facilitator</label><input className="inp" value={facilitator} onChange={(e) => setFacilitator(e.target.value)} placeholder="Who ran the drill" data-testid="ttx-facilitator" /></div>
          <div className="fld"><label>Participants</label><input className="inp" value={participants} onChange={(e) => setParticipants(e.target.value)} placeholder="Who was in the room" data-testid="ttx-participants" /></div>
        </div>
        <div className="fld" style={{ marginBottom: 12 }}>
          <label>The scenario</label>
          <textarea className="inp ta" rows={2} value={scenario} onChange={(e) => setScenario(e.target.value)} placeholder='e.g. "Provider&apos;s license expired last Tuesday and she worked two cases Thursday — what happens now?"' data-testid="ttx-scenario" />
        </div>
        <div className="fld" style={{ marginBottom: 12 }}>
          <label>What the manual says (SOP refs)</label>
          <textarea className="inp ta" rows={2} value={manualSays} onChange={(e) => setManualSays(e.target.value)} placeholder="The steps the manual prescribes for this scenario" data-testid="ttx-manual-says" />
        </div>
        <div className="fld" style={{ marginBottom: 12 }}>
          <label>What we actually did / could actually do</label>
          <textarea className="inp ta" rows={2} value={actual} onChange={(e) => setActual(e.target.value)} placeholder="Be blunt — where execution differed, stalled, or was impossible" data-testid="ttx-actual" />
        </div>
        <button className="btn" onClick={submit} disabled={!canSubmit} data-testid="ttx-submit">Log exercise</button>
      </div>

      <h2>Exercises {rows ? `— ${openCount} open` : ""}</h2>
      {rows === null ? (
        <p>Loading…</p>
      ) : rows.length === 0 ? (
        <p data-testid="ttx-empty">No exercises yet. First one to run: the license-lapse drill from the working session.</p>
      ) : (
        rows.map((tt) => <ExerciseCard key={tt.id} tt={tt} onSaved={onSaved} />)
      )}
      <p style={{ color: "var(--mute)", fontSize: "13px" }}>Drills and their gap closures appear in the Audit Trail and export with the binder — counsel sees the manual is exercised, not just read. "Change the Manual" gaps should also get a Change Request (FB) so the edit lands in the next version.</p>
    </section>
  );
}
